/**
 * NeedMappingsPage — admin CRUD for shopper "need" -> attribute-filter
 * mappings (PIM Wave P4 Task 7). Backend contract (Task 4, shipped): a
 * small, admin-curated, UN-PAGINATED list (`GET /admin/catalog/
 * need-mappings`), so this page uses a plain `useQuery` + the kit's
 * `AdminDataTable` — mirroring `FieldLibraryPage`/`BindingEditorPage` (the
 * two most recently-shipped PIM-wave admin surfaces), NOT the older
 * hand-rolled `seller-applications` chrome. `AdminDataTable` already owns
 * its own loading/error/empty states (`FieldLibraryPage` does not ALSO wrap
 * it in `AdminStates` — doing so here too would double-render the same
 * three states), so this page follows that same precedent instead of the
 * brief's literal "+ AdminStates" wording; a manual retry action renders
 * below the table on error, matching `FieldLibraryPage`'s own composition.
 *
 * One permission gates this whole surface (`marketplace.attribute.manage`
 * — same as every other `/admin/catalog/*` admin CRUD page), so — mirroring
 * `FieldLibraryPage`/`CategoryListPage` — there is no per-action
 * `useRequirePermission` check here; `RequirePermissionRoute` gates the
 * route itself (wired in `App.tsx`).
 *
 * Create/edit both open the same `MatcherBuilder` sheet (create = `mapping:
 * null`); delete is a hard DELETE (204, no soft-delete concept here — "pure
 * config, nothing references it" per the backend doc comment), confirmed
 * via a sibling `BottomSheet`, mirroring `FieldLibraryPage`'s archive-
 * confirm shape (this page is not itself nested inside another open
 * `BottomSheet`, so — unlike `GroupManager`'s inline confirm, which IS
 * always nested inside a `BottomSheet` — a real confirm `BottomSheet` is
 * safe here).
 *
 * The "field" column resolves `field_id` -> name via `useAllAttributeFields`
 * (the full drain, including archived — a persisted mapping may reference
 * an archived field, mirrors `BindingEditorPage`'s resolution-superset
 * rule). The "matcher" column's summary renders raw values
 * (`min – max` / the raw `value_slugs`) rather than resolving `option_set`
 * slugs to their localized option labels — that would need a per-row
 * options fetch for whichever field each row happens to reference (no
 * global "all options for all fields" endpoint exists); `MatcherBuilder`
 * resolves labels in the edit sheet, where the one relevant field's options
 * are already being fetched for editing.
 */
import { useMemo, useState, type ReactElement, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";

import { BottomSheet, Button, GPill } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { AdminDataTable, AdminPageHeader, type DataTableColumn } from "@/features/admin/_shared";
import type { AttributeField } from "@/features/admin/attribute-fields/api";
import { useAllAttributeFields } from "@/features/admin/attribute-fields/hooks";
import { fieldTypeMeta } from "@/features/admin/attribute-fields/lib/fieldTypeMeta";
import type { NeedMapping, NeedMappingMatcher } from "@/features/admin/need-mappings/api";
import { useDeleteNeedMapping, useNeedMappings } from "@/features/admin/need-mappings/hooks";
import { MatcherBuilder } from "@/features/admin/need-mappings/components/MatcherBuilder";
import { PendingChangesBanner } from "@/features/admin/changesets/components/PendingChangesBanner";

const ICON_BUTTON_CLASS =
  "grid h-9 w-9 place-items-center rounded-full text-[var(--g-secondary)] hover:bg-[var(--g-elevated)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]";

const EMPTY_MAPPINGS: readonly NeedMapping[] = [];
const EMPTY_FIELDS: readonly AttributeField[] = [];

function formatMatcherSummary(matcher: NeedMappingMatcher): string {
  if (matcher.kind === "numeric_range") {
    const { min, max } = matcher;
    if (min !== null && max !== null) return `${min} – ${max}`;
    if (min !== null) return `≥ ${min}`;
    if (max !== null) return `≤ ${max}`;
    return "—";
  }
  return matcher.value_slugs.length > 0 ? matcher.value_slugs.join(", ") : "—";
}

function phraseCount(phrases: Record<string, string[]>): number {
  return Object.values(phrases).reduce((sum, arr) => sum + (arr?.length ?? 0), 0);
}

export function NeedMappingsPage(): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);

  const { data, isLoading, isError, refetch } = useNeedMappings();
  const mappings = data?.need_mappings ?? EMPTY_MAPPINGS;

  const fieldsQuery = useAllAttributeFields();
  const fields = fieldsQuery.data ?? EMPTY_FIELDS;
  const fieldsById = useMemo(() => new Map(fields.map((f) => [f.id, f])), [fields]);

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<NeedMapping | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<NeedMapping | null>(null);

  const deleteMutation = useDeleteNeedMapping();

  const confirmDelete = (): void => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success(t("pim.needMappings.delete.success"));
        setDeleteTarget(null);
      },
      onError: () => toast.error(t("error.generic")),
    });
  };

  const columns: DataTableColumn[] = [
    { key: "slug", label: t("pim.needMappings.columns.slug") },
    { key: "field", label: t("pim.needMappings.columns.field") },
    { key: "matcher", label: t("pim.needMappings.columns.matcher") },
    { key: "phrases", label: t("pim.needMappings.columns.phrases"), align: "right" },
    { key: "weight", label: t("pim.needMappings.columns.weight"), align: "right" },
    { key: "actions", label: t("pim.needMappings.columns.actions") },
  ];

  const renderCell = (mapping: NeedMapping, key: string): ReactNode => {
    switch (key) {
      case "slug":
        return <span className="font-mono text-xs">{mapping.slug}</span>;
      case "field": {
        const field = fieldsById.get(mapping.field_id);
        const meta = field ? fieldTypeMeta(field.field_type) : undefined;
        return (
          <div className="flex flex-col gap-0.5">
            <span className="font-medium">{field?.name_ru ?? mapping.field_id.slice(0, 8)}</span>
            {meta ? <GPill tone="neutral" size="sm">{t(meta.labelKey)}</GPill> : null}
          </div>
        );
      }
      case "matcher":
        return (
          <div className="flex flex-col gap-0.5">
            <GPill tone="primary" size="sm">
              {t(`pim.needMappings.matcherKind.${mapping.matcher.kind}`)}
            </GPill>
            <span className="text-xs text-[var(--g-secondary)]">
              {formatMatcherSummary(mapping.matcher)}
            </span>
          </div>
        );
      case "phrases":
        return String(phraseCount(mapping.phrases));
      case "weight":
        return mapping.weight;
      case "actions":
        return (
          <div className="flex items-center gap-1">
            <button
              type="button"
              data-testid={`pim-need-mapping-edit-${mapping.id}`}
              aria-label={t("pim.needMappings.actions.edit")}
              onClick={(event) => {
                event.stopPropagation();
                setEditing(mapping);
              }}
              className={ICON_BUTTON_CLASS}
            >
              <Pencil size={16} strokeWidth={1.75} aria-hidden />
            </button>
            <button
              type="button"
              data-testid={`pim-need-mapping-delete-${mapping.id}`}
              aria-label={t("pim.needMappings.actions.delete")}
              onClick={(event) => {
                event.stopPropagation();
                setDeleteTarget(mapping);
              }}
              className={ICON_BUTTON_CLASS}
            >
              <Trash2 size={16} strokeWidth={1.75} aria-hidden />
            </button>
          </div>
        );
      default:
        return "";
    }
  };

  const renderMobileCard = (mapping: NeedMapping): ReactElement => {
    const field = fieldsById.get(mapping.field_id);
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <span className="font-medium">{field?.name_ru ?? mapping.field_id.slice(0, 8)}</span>
          <GPill tone="primary" size="sm">
            {t(`pim.needMappings.matcherKind.${mapping.matcher.kind}`)}
          </GPill>
        </div>
        <span className="font-mono text-xs text-[var(--g-secondary)]">{mapping.slug}</span>
        <span className="text-xs text-[var(--g-secondary)]">
          {formatMatcherSummary(mapping.matcher)}
        </span>
        <div className="flex items-center justify-between text-xs text-[var(--g-secondary)]">
          <span>
            {t("pim.needMappings.columns.phrases")}: {phraseCount(mapping.phrases)}
          </span>
          <span>
            {t("pim.needMappings.columns.weight")}: {mapping.weight}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            data-testid={`pim-need-mapping-card-edit-${mapping.id}`}
            aria-label={t("pim.needMappings.actions.edit")}
            onClick={(event) => {
              event.stopPropagation();
              setEditing(mapping);
            }}
            className={ICON_BUTTON_CLASS}
          >
            <Pencil size={16} strokeWidth={1.75} aria-hidden />
          </button>
          <button
            type="button"
            data-testid={`pim-need-mapping-card-delete-${mapping.id}`}
            aria-label={t("pim.needMappings.actions.delete")}
            onClick={(event) => {
              event.stopPropagation();
              setDeleteTarget(mapping);
            }}
            className={ICON_BUTTON_CLASS}
          >
            <Trash2 size={16} strokeWidth={1.75} aria-hidden />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="need-mappings-page" className="flex flex-col gap-4">
      <AdminPageHeader
        title={t("pim.needMappings.title")}
        subtitle={t("pim.needMappings.subtitle")}
        actions={
          <Button
            variant="primary"
            size="md"
            leading="plus"
            data-testid="pim-need-mappings-create"
            onClick={() => setCreating(true)}
            className="min-h-[44px]"
          >
            {t("pim.needMappings.create")}
          </Button>
        }
      />

      <PendingChangesBanner />

      <AdminDataTable<NeedMapping>
        columns={columns}
        rows={mappings}
        rowKey={(mapping) => mapping.id}
        renderCell={renderCell}
        renderMobileCard={renderMobileCard}
        onRowActivate={(mapping) => setEditing(mapping)}
        rowAriaLabel={(mapping) => mapping.slug}
        testid="pim-need-mappings"
        isLoading={isLoading}
        isError={isError}
        emptyLabel={t("pim.needMappings.empty")}
        errorLabel={t("pim.needMappings.error")}
      />

      {isError ? (
        <div className="flex justify-center">
          <Button
            variant="ghost"
            size="sm"
            data-testid="pim-need-mappings-retry"
            onClick={() => void refetch()}
            className="min-h-[44px]"
          >
            {t("pim.needMappings.retry")}
          </Button>
        </div>
      ) : null}

      <MatcherBuilder
        open={creating || editing !== null}
        mapping={editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
      />

      <BottomSheet
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title={t("pim.needMappings.delete.confirmTitle")}
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-[var(--g-secondary)]">
            {t("pim.needMappings.delete.confirmBody", { slug: deleteTarget?.slug ?? "" })}
          </p>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDeleteTarget(null)}
              disabled={deleteMutation.isPending}
              className="min-h-[44px]"
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              data-testid="pim-need-mappings-delete-confirm"
              loading={deleteMutation.isPending}
              onClick={confirmDelete}
              className="min-h-[44px]"
            >
              {t("pim.needMappings.delete.confirm")}
            </Button>
          </div>
        </div>
      </BottomSheet>
    </div>
  );
}

export default NeedMappingsPage;
